import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { cn } from "../../lib/utils";

interface DropdownMenuProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface DropdownMenuTriggerProps {
  asChild?: boolean;
  children: React.ReactNode;
}

interface DropdownMenuContentProps {
  align?: "start" | "end" | "center";
  className?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}

interface DropdownMenuItemProps {
  className?: string;
  onClick?: () => void;
  children: React.ReactNode;
}

const DropdownMenuContext = React.createContext<{
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLElement>;
  triggerRect: DOMRect | null;
} | null>(null);

export function DropdownMenu({ children }: DropdownMenuProps) {
  const [open, setOpen] = useState(false);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (open && triggerRef.current) {
      // Recalculate position when opening
      setTriggerRect(triggerRef.current.getBoundingClientRect());
    } else if (!open) {
      // Clear rect when closing
      setTriggerRect(null);
    }
  }, [open]);

  return (
    <DropdownMenuContext.Provider value={{ open, setOpen, triggerRef, triggerRect }}>
      <div className="relative inline-block text-left">
        {children}
      </div>
    </DropdownMenuContext.Provider>
  );
}

export function DropdownMenuTrigger({ asChild, children }: DropdownMenuTriggerProps) {
  const context = React.useContext(DropdownMenuContext);
  if (!context) throw new Error("DropdownMenuTrigger must be used within DropdownMenu");

  const { open, setOpen, triggerRef } = context;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(!open);
  };

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as any, {
      ...children.props,
      ref: triggerRef,
      onClick: handleClick,
    });
  }

  return (
    <button ref={triggerRef as any} onClick={handleClick}>
      {children}
    </button>
  );
}

function DropdownMenuPortal({ children, triggerRect, align = "start" }: { 
  children: React.ReactNode; 
  triggerRect: DOMRect | null;
  align?: "start" | "end" | "center";
}) {
  if (!triggerRect) return null;

  const style: React.CSSProperties = {
    position: 'fixed',
    zIndex: 9999,
    top: triggerRect.bottom + 4,
  };

  if (align === 'end') {
    style.left = triggerRect.right;
    style.transform = 'translateX(-100%)';
  } else if (align === 'center') {
    style.left = triggerRect.left + triggerRect.width / 2;
    style.transform = 'translateX(-50%)';
  } else {
    style.left = triggerRect.left;
  }

  return ReactDOM.createPortal(
    <div style={style}>
      {children}
    </div>,
    document.body
  );
}

export function DropdownMenuContent({ align = "start", className, children, style }: DropdownMenuContentProps) {
  const context = React.useContext(DropdownMenuContext);
  if (!context) throw new Error("DropdownMenuContent must be used within DropdownMenu");

  const { open, setOpen, triggerRect, triggerRef } = context;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is outside the dropdown content AND not on the trigger
      const clickedOutsideContent = ref.current && !ref.current.contains(event.target as Node);
      const clickedOnTrigger = triggerRef.current && triggerRef.current.contains(event.target as Node);
      
      if (clickedOutsideContent && !clickedOnTrigger) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, setOpen, triggerRef]);

  if (!open) return null;

  return (
    <DropdownMenuPortal triggerRect={triggerRect} align={align}>
      <div
        ref={ref}
        className={cn(
          "min-w-[8rem] rounded-md border border-gray-200 bg-white p-1 shadow-md",
          className
        )}
        style={style}
      >
        {children}
      </div>
    </DropdownMenuPortal>
  );
}

export function DropdownMenuItem({ className, onClick, children }: DropdownMenuItemProps) {
  const context = React.useContext(DropdownMenuContext);
  if (!context) throw new Error("DropdownMenuItem must be used within DropdownMenu");

  const { setOpen } = context;

  const handleClick = () => {
    onClick?.();
    setOpen(false);
  };

  return (
    <button
      className={cn(
        "relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-gray-100 focus:bg-gray-100 data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      )}
      onClick={handleClick}
    >
      {children}
    </button>
  );
}